import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import dynamic from 'next/dynamic';
import isObject from 'lodash/isObject';
import valuesIn from 'lodash/valuesIn';
import { getIn } from 'formik';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import InputLabel from '@material-ui/core/InputLabel';
import Popper from '@material-ui/core/Popper';
import { FormControl } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { borderRadius, colors, fontSize } from '../../constants/styleConstants';
import { withTranslation } from '../../src/i18n';
import { selectOptionsPropType, selectOptionsWithGroupsPropType } from './FormikAutocompleteSelect';

// dynamic imports
const FormHelperText = dynamic(() => import('@material-ui/core/FormHelperText'));
const TextField = dynamic(() => import('@material-ui/core/TextField'));
const ArrowDropDown = dynamic(() => import('@material-ui/icons/ArrowDropDown'));
const ExpandMoreIcon = dynamic(() => import('@material-ui/icons/ExpandMore'));

// Autocomplete styles
const useStyles = makeStyles(() => ({
  root: {
    marginTop: props => props.label && 16,
  },
  clearIndicator: {
    color: colors.blue,
    marginRight: 5,
    '& svg': {
      fontSize: '1.0rem',
    },
  },
  label: {
    lineHeight: 'calc(1em + 2px) !important',
    fontSize: `${fontSize.smallTitle}px !important`,
    transform: 'translate(0, 1.5px) scale(1) !important',
    width: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden !important',
    textOverflow: 'ellipsis',
    color: colors.title,
    letterSpacing: '0.6px',
  },
  textArea: {
    userSelect: 'none',
    background: colors.white,
    '& .MuiOutlinedInput-notchedOutline': {
      top: 0,
      '& legend': {
        display: 'none',
      },
    },
    '& input': {
      fontSize: '16px !important',
    },
    '& input::-webkit-search-cancel-button, & input::-webkit-search-decoration, & input::-webkit-inner-spin-button': {
      display: 'none',
    },
  },
  rectangle: {
    height: 45,
    '& .MuiOutlinedInput-notchedOutline': {
      border: '1px solid #b3b3b3 !important',
      borderRadius: borderRadius.fields,
    },
  },
  oval: {
    height: 37,
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'solid 1px rgb(223, 223, 223) !important',
      borderRadius: 7,
    },
  },
  popupIndicator: {
    '&:hover': {
      backgroundColor: colors.white,
    },
  },
}));

const styles = {
  controlIcon: {
    display: 'flex',
    transition: 'color 150ms ease 0s',
    boxSizing: 'border-box',
    cursor: 'pointer',
    zIndex: 1,
    backgroundColor: colors.white,
    marginRight: 5,
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
      backgroundColor: colors.white,
    },
  },
  icon: {
    color: colors.blue,
  },
};

const customStyles = makeStyles(styles);

const DropdownIndicator = props => {
  const classes = customStyles();
  const { customIndicator, noIndicator, isDisabled } = props;
  if (isDisabled || noIndicator) {
    return null;
  }

  return (
    <div className={classes.controlIcon}>
      {customIndicator === false ? (
        <ArrowDropDown style={{ fill: colors.blueChip }} />
      ) : (
        <ExpandMoreIcon className={classes.icon} />
      )}
    </div>
  );
};

DropdownIndicator.propTypes = {
  isDisabled: PropTypes.bool,
  noIndicator: PropTypes.bool,
  customIndicator: PropTypes.bool,
};

const AutocompleteSelect = ({
  t,
  isCreatable,
  isEditable,
  isMulti,
  required,
  label,
  field: { name, value, placeholder },
  form: { errors, touched, setFieldValue, setFieldTouched },
  options,
  minSearchCharsLength,
  customNoResultText,
  customComponents,
  onInputTextChange,
  resetOptions,
  onFocus,
  children,
  onChange,
  isLoading,
  instanceId,
  inputLabelProps,
  inputProps,
  preventDefaultOnChange,
  withFiltering,
  className,
  isCustomPopper,
  popperWidth,
  ...other
}) => {
  const [inputValue, setInputValue] = React.useState(value?.label);
  const classes = useStyles({ ...other.customIndicator, ...other.noIndicator, label });
  const [open, setOpen] = React.useState(false);

  const isMinSearchValid = minSearchValue =>
    (minSearchCharsLength && minSearchValue.length >= minSearchCharsLength) || !minSearchCharsLength;

  const hasError = Boolean(getIn(touched, name) && getIn(errors, name));
  const errorText = getIn(errors, name);

  const customPopper = props => <Popper {...props} style={{ width: popperWidth }} placement='bottom-start' />;

  const onBlurMenuClose = () => {
    resetOptions(name);
  };
  const getFilterOption = () => {
    if (withFiltering) {
      createFilterOptions({
        stringify: other.filterOption ? { filterOptions: other.filterOption } : {},
      });
    } else {
      createFilterOptions({
        stringify: option => option.title,
      });
    }
  };

  const noOptionsMessage = () => {
    if ((!other.startAggregations && !isLoading && options.length === 0) || other.error) {
      return customNoResultText || 'No options';
    }
  };

  return (
    <FormControl fullWidth error={hasError} className={classnames(classes.field, className)}>
      <InputLabel classes={{ root: classes.label }} shrink htmlFor="bootstrap-input">
        {label}
      </InputLabel>
      <Autocomplete
        value={value !== undefined ? value : null}
        onFocus={() => {
          if (!minSearchCharsLength) {
            setOpen(true);
          }
          if (onFocus) {
            onFocus(value);
          }
        }}
        forcePopupIcon={other.customIndicator || !other.noIndicator}
        popupIcon={<DropdownIndicator {...other} />}
        classes={{
          root: classes.root,
          clearIndicator: classes.clearIndicator,
          popupIndicator: classes.popupIndicator,
          endAdornment: classes.endAdornment,
        }}
        onInputChange={(e, newInputValue, reason) => {
          if (isMinSearchValid(newInputValue) && onInputTextChange && reason !== 'reset') {
            onInputTextChange(newInputValue);
            setOpen(newInputValue?.length > 0);
          }
        }}
        inputValue={inputValue}
        onChange={(event, newValue) => {
          setInputValue(newValue?.label || '');
          if (!preventDefaultOnChange) {
            setFieldValue(name, newValue);
          }
          if (newValue) {
            onChange(newValue);
          }
        }}
        getOptionSelected={(option, val) => option.value === val.value}
        filterOptions={getFilterOption()}
        blurOnSelect
        onBlur={() => {
          setOpen(false);
          if (isEditable) {
            onBlurMenuClose();
          }
        }}
        clearOnBlur
        handleHomeEndKeys
        id={instanceId}
        options={options}
        multiple={isMulti}
        getOptionLabel={option => {
          if (typeof option === 'string') {
            return option;
          }
          if (option.inputValue) {
            return option.inputValue;
          }
          return option.label;
        }}
        name={name}
        renderOption={option => option.label}
        loading={isLoading || other.startAggregations}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        {...(isCustomPopper && { PopperComponent: customPopper })}
        disableClearable={!other.isClearable}
        noOptionsText={noOptionsMessage()}
        renderInput={params => (
          <TextField
            {...params}
            required={required}
            InputProps={{
              autoComplete: 'false',
              type: 'search',
              ...params.InputProps,
              className: classnames(classes.textArea, other.newDropdown ? classes.oval : classes.rectangle),
            }}
            variant="outlined"
          />
        )}
      />
      {children}
      {errorText && (
        <FormHelperText
          className={classnames(classes.formHelperText, {
            [classes.warningColor]: other.warning,
            [classes.errorColor]: hasError,
          })}
        >
          {/* to set inner values errors of  select options */}
          {isObject(errorText) && !errorText.props ? valuesIn(errorText) : t(errorText)}
        </FormHelperText>
      )}
    </FormControl>
  );
};

AutocompleteSelect.propTypes = {
  t: PropTypes.func.isRequired,
  instanceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  options: PropTypes.oneOfType([
    selectOptionsPropType,
    selectOptionsWithGroupsPropType,
    PropTypes.arrayOf(PropTypes.object),
  ]).isRequired,
  children: PropTypes.node,
  minSearchCharsLength: PropTypes.number,
  classes: PropTypes.object,
  customIndicator: PropTypes.bool,
  isCreatable: PropTypes.bool,
  isEditable: PropTypes.bool,
  theme: PropTypes.object,
  selectTextColor: PropTypes.string,
  isMulti: PropTypes.bool,
  isAsyncSelect: PropTypes.bool,
  required: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  customNoResultText: PropTypes.string,
  customComponents: PropTypes.object,
  field: PropTypes.shape({
    name: PropTypes.string,
    onChange: PropTypes.func,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array, PropTypes.object]),
  }),
  form: PropTypes.shape({
    dirty: PropTypes.bool,
    errors: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    setFieldTouched: PropTypes.func,
    setFieldValue: PropTypes.func,
  }),
  onChange: PropTypes.func,
  onInputTextChange: PropTypes.func,
  onFocus: PropTypes.func,
  isLoading: PropTypes.bool,
  preventDefaultOnChange: PropTypes.bool,
  noIndicator: PropTypes.bool,
  resetOptions: PropTypes.func,
  withFiltering: PropTypes.bool,
  staticOption: PropTypes.oneOfType([PropTypes.bool, PropTypes.element]),
  newDropdown: PropTypes.bool,
  isCustomPopper: PropTypes.bool,
  popperWidth: PropTypes.number,
};

AutocompleteSelect.defaultProps = {
  customIndicator: false,
  isMulti: false,
  isAsyncSelect: false,
  minSearchCharsLength: 0,
  onInputTextChange: () => {},
  onChange: () => {},
  resetOptions: () => {},
  children: undefined,
  form: {
    dirty: false,
    errors: [],
    setFieldValue: () => {},
    setFieldTouched: () => {},
  },
  isLoading: false,
  required: false,
  preventDefaultOnChange: false,
  isEditable: false,
  onFocus: () => {},
  noIndicator: false,
  withFiltering: false,
  newDropdown: false,
};

export default withTranslation()(AutocompleteSelect);
